import { useId, useState } from "react"

// ** Third Party Components
import { ChevronDown } from "lucide-react"

// ** Internal Elements
import { Input } from "@components/common/input"
import { Textarea } from "@components/common/textarea"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/common/dropdown-menu"

// ** Types
import { BilingualInputProps, DictType } from "@pma/types"

// ** Helpers
import { cn } from "@pma/utils/helpers"
import { locales } from "@pma/locale/dictionaries"

// ** Hooks
import useTranslator from "@pma/hooks/use-translator"

export function BilingualInput({
  id: externalId,
  value,
  onChange,
  placeholder = { en: "English", he: "Hebrew" },
  className,
  type = "input",
  textareaRows = 2,
  required = false,
}: BilingualInputProps) {
  const internalId = useId()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const [activeLang, setActiveLang] = useState<"en" | "he">(locale)

  const id = externalId || internalId

  const langInfo: DictType = {
    en: { emoji: "🇺🇸", label: "EN" },
    he: { emoji: "🇮🇱", label: "HE" },
  }

  const languages = locales

  const handleChange = (lang: "en" | "he", newValue: string) => {
    onChange({ ...value, [lang]: newValue })
  }

  return (
    <div className={cn("flex items-center gap-1", className, isHebrew ? "rtl" : "ltr")}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            className={cn(
              "flex h-8 items-center border bg-background px-1 text-xs font-medium hover:bg-muted/50",
              isHebrew ? "rtl rounded-r-md" : "ltr rounded-l-md",
            )}
          >
            <span className={cn(isHebrew ? "ml-1" : "mr-1")}>{langInfo[activeLang].emoji}</span>
            {langInfo[activeLang].label}
            <ChevronDown className={cn("h-3 w-3 opacity-70", isHebrew ? "mr-1" : "ml-1")} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start' className='min-w-[100px]'>
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.locale}
              className={cn(
                "cursor-pointer text-xs",
                activeLang === lang.locale && "bg-muted font-medium",
                isHebrew ? "rtl" : "ltr",
              )}
              onClick={() => setActiveLang(lang.locale as "en" | "he")}
            >
              <span className={cn(isHebrew ? "ml-2" : "mr-2")}>{lang.emoji}</span>
              {translate(lang.name)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className='flex-1'>
        {activeLang === "en" ? (
          type === "input" ? (
            <Input
              id={`${id}-en`}
              value={value.en}
              onChange={(e) => handleChange("en", e.target.value)}
              placeholder={placeholder.en}
              required={required}
              className={cn("h-8 text-sm", isHebrew ? "rounded-r-none" : "rounded-l-none")}
            />
          ) : (
            <Textarea
              id={`${id}-en`}
              value={value.en}
              onChange={(e) => handleChange("en", e.target.value)}
              placeholder={placeholder.en}
              required={required}
              rows={textareaRows}
              className={cn("min-h-0 py-1 text-sm", isHebrew ? "rounded-r-none" : "rounded-l-none")}
            />
          )
        ) : type === "input" ? (
          <Input
            id={`${id}-he`}
            value={value.he}
            onChange={(e) => handleChange("he", e.target.value)}
            placeholder={placeholder.he}
            required={required}
            className={cn("h-8 text-sm", isHebrew ? "rounded-r-none" : "rounded-l-none")}
          />
        ) : (
          <Textarea
            id={`${id}-he`}
            value={value.he}
            onChange={(e) => handleChange("he", e.target.value)}
            placeholder={placeholder.he}
            required={required}
            rows={textareaRows}
            className={cn("min-h-0 py-1 text-sm", isHebrew ? "rounded-r-none" : "rounded-l-none")}
          />
        )}
      </div>
    </div>
  )
}
